import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import debounce from "lodash/debounce";
import { useQuery } from "@apollo/client/react";
import { SIMPLE_INVENTORY_REQUESTS_QUERY } from "../constants/graphql/queries";
import useLabels from "../hooks/useLabels";
import OutboundTableSkeleton from "./project/outbound/OutboundTableSkeleton";
import OutboundTable from "./project/outbound/OutboundTable";

export default function OutboundSummaryModal({ asset, onClose }) {
  const l = useLabels();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(asset.itemId);
  const [debouncedSearch, setDebouncedSearch] = useState(asset.itemId);
  const showDeliveryDate = asset?.project?.fieldSettings?.order_delivery_date;
  const showOrderComments = asset?.project?.fieldSettings?.order_comments;
  const {
    loading,
    data: { inventoryRequests = {} } = {},
    refetch,
  } = useQuery(SIMPLE_INVENTORY_REQUESTS_QUERY, { variables: { itemId: debouncedSearch } });

  const list = inventoryRequests?.collection || [];
  const { currentPage, totalPages, totalCount } = inventoryRequests?.metadata || {};

  const debouncedSetSearch = useCallback(
    debounce(value => {
      setDebouncedSearch(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchInput);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchInput, debouncedSetSearch]);

  const handleSearchChange = event => {
    setSearchInput(event.target.value);
  };

  const handleToggle = useCallback(
    shipment => {
      const pathname = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);

      if (searchParams.get("receiving")) {
        navigate({ pathname, search: "" });
        return;
      }

      searchParams.set("step", "shipment");
      searchParams.set("receiving", "true");
      searchParams.set("editShipment", "true");
      searchParams.set("shipmentId", shipment.shipmentId);
      searchParams.set("id", shipment.id);
      navigate({ pathname, search: searchParams.toString() });
    },
    [navigate]
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography fontWeight={500} fontSize={22} color="textPrimary" align="center">
          {l.identifiers.outbound_summary}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search"
            size="small"
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {loading ? (
          <OutboundTableSkeleton />
        ) : list.length > 0 ? (
          <OutboundTable
            list={list}
            refetch={refetch}
            onEdit={handleToggle}
            page={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={undefined}
            showDeliveryDate={showDeliveryDate}
            showOrderComments={showOrderComments}
          />
        ) : (
          <Box p={3} textAlign="center">
            <Typography variant="body1">No outbound items found.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
